import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { LowTonnageReason } from "@/pages/caster/models/catalogs/low-tonnage-reasons";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import React, { useState, useRef } from "react";
import { UpsertLowTonnageReason } from "./UpsertLowTonnageReason";
import { useTableData } from "./hooks/useTableData";

type Props = {
	lowTonnageReasons: LowTonnageReason[];
	isLoading: boolean;
	onRefresh: () => void;
};

export const TableLowTonnageReasons: React.FC<Props> = ({
	lowTonnageReasons,
	isLoading,
	onRefresh,
}) => {
	const classes = useGridStyles();
	const [quickFilterText, setQuickFilterText] = useState("");
	const gridRef = useRef<AgGridReact>(null);

	// Modal state
	const [modalState, setModalState] = useState<
		"create" | "edit" | "delete" | null
	>(null);
	const [selectedLowTonnageReason, setSelectedLowTonnageReason] =
		useState<LowTonnageReason | null>(null);

	const handleCreateClick = () => {
		setSelectedLowTonnageReason(null);
		setModalState("create");
	};

	const handleEditClick = (lowTonnageReasonId: number) => {
		const lowTonnageReason = lowTonnageReasons.find(
			(l) => l.LowTonnageReasonId === lowTonnageReasonId,
		);
		if (lowTonnageReason) {
			setSelectedLowTonnageReason(lowTonnageReason);
			setModalState("edit");
		}
	};

	const handleDeleteClick = (lowTonnageReasonId: number) => {
		const lowTonnageReason = lowTonnageReasons.find(
			(l) => l.LowTonnageReasonId === lowTonnageReasonId,
		);
		if (lowTonnageReason) {
			setSelectedLowTonnageReason(lowTonnageReason);
			setModalState("delete");
		}
	};

	const handleModalClose = () => {
		setModalState(null);
		setSelectedLowTonnageReason(null);
	};

	const handleModalSuccess = () => {
		onRefresh();
		handleModalClose();
	};

	const { columns } = useTableData({
		onDeleteButtonClick: handleDeleteClick,
		onEditButtonClick: handleEditClick,
	});

	React.useEffect(() => {
		if (gridRef.current) {
			if (isLoading) {
				gridRef.current.api?.showLoadingOverlay();
			} else {
				gridRef.current.api?.hideOverlay();
			}
		}
	}, [isLoading]);

	return (
		<>
			<Grid size={{ xs: 12 }} container justifyContent="center" spacing={1}>
				<Grid size={{ xs: 12, md: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Low Tonnage Reasons
					</Typography>
				</Grid>

				<Grid
					size={{ xs: 12 }}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search low tonnage reasons"
						/>
					</Grid>
					<Grid
						size={{ xs: 12, md: 12 }}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								ref={gridRef}
								rowData={lowTonnageReasons}
								columnDefs={columns}
								getRowId={(params) => params.data.LowTonnageReasonId}
								suppressContextMenu
								onRowDoubleClicked={(params) => {
									handleEditClick(params.data.LowTonnageReasonId);
								}}
								rowSelection="single"
								quickFilterText={quickFilterText}
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, md: "auto" }} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={handleCreateClick}
						>
							NEW LOW TONNAGE REASON
						</Button>
					</Grid>
				</Grid>
			</Grid>

			{modalState && (
				<UpsertLowTonnageReason
					open={modalState !== null}
					mode={modalState}
					lowTonnageReason={selectedLowTonnageReason}
					onClose={handleModalClose}
					onSuccess={handleModalSuccess}
				/>
			)}
		</>
	);
};
